import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '@/utils/api';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { SearchableSelect } from './ui/searchable-select';
import { CreatableSelect } from './ui/creatable-select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter, SheetTrigger } from './ui/sheet';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { DateRangeFilter } from './ui/date-range-filter';
import { toast } from 'sonner';
import { Phone, PhoneOff, MessageCircle, Star, Clock, Bell, Calendar, XCircle, Users, ClipboardEdit, Download, Plus, Search, Filter, GraduationCap, RefreshCw, Check, AlertTriangle, SlidersHorizontal, X, ChevronLeft, ChevronRight, UserPlus, AlertCircle } from 'lucide-react';
import { COUNTRIES } from '@/utils/constants';
import CallResponseDialog from './CallResponseDialog';

// Design tokens from logo colors
const COLORS = {
  primary: '#192650',
  accent: '#0DA891',
  accentLight: '#E8F7F5',
  background: '#F8FAFC',
  cardBg: '#FFFFFF',
  textSecondary: '#64748B',
  border: '#E2E8F0',
};

// Tab definitions based on CRM WORKFLOW SPEC
const TABS = [
  { id: 'new', label: 'New', icon: Bell, color: '#3B82F6' },
  { id: 'overdue', label: 'Overdue', icon: Clock, color: '#EF4444' },
  { id: 'followups', label: 'Follow-ups', icon: Calendar, color: '#64748B' },
  { id: 'rnr', label: 'RNR', icon: PhoneOff, color: '#F97316' },
  { id: 'review', label: 'Review', icon: AlertTriangle, color: '#8B5CF6' },
  { id: 'future', label: 'Future', icon: Clock, color: '#F59E0B' },
  { id: 'bad', label: 'Bad', icon: XCircle, color: '#6B7280' }
];

// Call response options
const CALL_RESPONSES = [
  { value: 'connected', label: 'Connected', icon: '✅', requiresNotes: true },
  { value: 'not_answered', label: 'Not Answered', icon: '❌', requiresNotes: false },
  { value: 'busy', label: 'Busy', icon: '❌', requiresNotes: false },
  { value: 'switched_off', label: 'Switched Off', icon: '❌', requiresNotes: false },
  { value: 'not_reachable', label: 'Not Reachable', icon: '❌', requiresNotes: false }
];

// Status badge styles
const STATUS_STYLES = {
  new: { bg: '#EFF6FF', border: '#BFDBFE', text: '#1D4ED8', badgeBg: '#DBEAFE' },
  overdue: { bg: '#FEF2F2', border: '#FECACA', text: '#DC2626', badgeBg: '#FEE2E2' },
  followups: { bg: '#F8FAFC', border: '#E2E8F0', text: '#475569', badgeBg: '#F1F5F9' },
  rnr: { bg: '#FFF7ED', border: '#FDBA74', text: '#EA580C', badgeBg: '#FFEDD5' },
  future: { bg: '#FFFBEB', border: '#FDE68A', text: '#D97706', badgeBg: '#FEF3C7' },
  bad: { bg: '#F9FAFB', border: '#E5E7EB', text: '#6B7280', badgeBg: '#F3F4F6' }
};

export default function LeadsTileView() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState([]);
  const [tabCounts, setTabCounts] = useState({
    new: 0, overdue: 0, followups: 0, rnr: 0, future: 0, bad: 0
  });
  
  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 50,
    total_count: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false
  });
  
  // Current tab — supports deep-linking from the dashboard KPI cards (?tab=..&prospective=1)
  const _initialParams = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
  const _validTabs = ['new', 'overdue', 'followups', 'rnr', 'review', 'future', 'bad'];
  const _urlTab = _initialParams.get('tab');
  const [activeTab, setActiveTab] = useState(_validTabs.includes(_urlTab) ? _urlTab : 'new');

  // Overdue tab — sub-filter by overdue reason (Feb 2026 user request: spot
  // legacy stuck-in-overdue leads from before the mandatory-follow-up era).
  // Values: 'all' | 'never_contacted' | 'missed_followup' | 'missed_rnr' | 'missed_future'.
  const [overdueTypeFilter, setOverdueTypeFilter] = useState('all');

  // RNR threshold (configurable)
  const [rnrThreshold, setRnrThreshold] = useState(5);
  
  // Power Hours state
  const [powerHoursStatus, setPowerHoursStatus] = useState(null);
  const [powerHoursLeads, setPowerHoursLeads] = useState([]);
  const [claimingLead, setClaimingLead] = useState(null);
  const [poolTick, setPoolTick] = useState(0);  // Ticks every second to refresh countdown
  const [autoAssigningAll, setAutoAssigningAll] = useState(false);
  
  // Live countdown ticker — only when pool mode is active
  useEffect(() => {
    if (!powerHoursStatus?.pool_mode_active && !powerHoursStatus?.is_active) return;
    const t = setInterval(() => setPoolTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, [powerHoursStatus?.pool_mode_active, powerHoursStatus?.is_active]);
  
  // Compute remaining seconds for a pool lead (returns null if no expiry)
  const getRemainingSec = (lead) => {
    if (!lead?.pool_claim_expires_at) return null;
    const expires = new Date(lead.pool_claim_expires_at).getTime();
    const diffSec = Math.floor((expires - Date.now()) / 1000);
    return diffSec;
  };
  const formatCountdown = (sec) => {
    if (sec === null || sec === undefined) return '';
    if (sec <= 0) return 'auto-assigning…';
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };
  
  // Filters
  const [search, setSearch] = useState('');
  const [showProspective, setShowProspective] = useState(_initialParams.get('prospective') === '1');
  const [countryFilter, setCountryFilter] = useState('all');
  const [programFilter, setProgramFilter] = useState('all');
  const [counsellorFilter, setCounsellorFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState({ startDate: null, endDate: null, preset: 'all' });
  const [followupDateFilter, setFollowupDateFilter] = useState({ startDate: null, endDate: null, preset: 'all' });
  
  // Counsellors list (for admin filter)
  const [counsellors, setCounsellors] = useState([]);
  
  // Dynamic filter options (populated from database)
  const [filterCountries, setFilterCountries] = useState([]);
  const [filterPrograms, setFilterPrograms] = useState([]);
  const [loadingFilters, setLoadingFilters] = useState(false);
  
  // Mobile filter sheet state
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  
  // Add Lead Dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newLead, setNewLead] = useState({
    full_name: '',
    phone: '',
    email: '',
    lead_source: 'manual',
    desired_country: '',
    desired_course: '',
    eligible_intake: '',
    notes: '',
    assign_to_counselor_id: null
  });
  const [addingLead, setAddingLead] = useState(false);

  // Call Response Dialog
  const [callDialogOpen, setCallDialogOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  
  // Export state
  const [exporting, setExporting] = useState(false);

  // Clear Bad Leads (admin)
  const [showClearBad, setShowClearBad] = useState(false);
  const [clearForm, setClearForm] = useState({ date_field: 'created_at', from_date: '', to_date: '' });
  const [clearCount, setClearCount] = useState(null);
  const [clearBusy, setClearBusy] = useState(false);
  const [clearDownloaded, setClearDownloaded] = useState(false);
  
  // Countdown timer state - updates every second
  const [, forceUpdate] = useState(0);
  
  // Update component every second for countdown
  useEffect(() => {
    const timer = setInterval(() => {
      forceUpdate(n => n + 1);
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Fetch RNR threshold setting
  useEffect(() => {
    apiClient.get('/settings/rnr-threshold')
      .then(res => setRnrThreshold(res.data.rnr_threshold || 5))
      .catch(() => {});
  }, []);

  
  // ============================================================================
  // TIMEZONE HANDLING - Zero Conversion: dates are naive IST, treat as local
  // ============================================================================
  
  // Helper: Get elapsed time in milliseconds since a date (stored as IST)
  const getElapsedMs = (dateStr) => {
    if (!dateStr) return null;
    try {
      // Parse as local time (IST) - do NOT append Z
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return null;
      return Date.now() - date.getTime();
    } catch {
      return null;
    }
  };
  
  // Helper: Format a date string for display
  // Dates are naive IST - parse as local time, no conversion
  const formatToIST = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return 'N/A';
      
      const day = date.getDate();
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = months[date.getMonth()];
      
      let hours = date.getHours();
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12 || 12;
      
      return `${day} ${month} at ${hours.toString().padStart(2, '0')}:${minutes} ${ampm}`;
    } catch {
      return 'N/A';
    }
  };
  
  // ============================================================================
  // NEW TAB: 15-minute countdown for fresh leads ONLY
  // ============================================================================
  
  // Get countdown info for NEW leads only (within 15 minutes of creation)
  const getNewLeadCountdown = (lead) => {
    // Only for NEW tab - fresh leads with no calls
    if ((lead.total_calls || 0) > 0) return null;
    
    const elapsedMs = getElapsedMs(lead.created_at || lead.assigned_at);
    if (elapsedMs === null) return null;
    
    const elapsedSeconds = Math.floor(elapsedMs / 1000);
    const totalSeconds = 15 * 60; // 15 minutes
    const remainingSeconds = totalSeconds - elapsedSeconds;
    
    // Only show countdown if within 15 minutes
    if (remainingSeconds <= 0) return null;
    
    const mins = Math.floor(remainingSeconds / 60);
    const secs = remainingSeconds % 60;
    
    return {
      display: `${mins}:${secs.toString().padStart(2, '0')}`,
      isUrgent: remainingSeconds <= 5 * 60,
      isCritical: remainingSeconds <= 2 * 60
    };
  };
  
  // Get age display for leads (how old the lead is)
  const getLeadAge = (lead) => {
    const elapsedMs = getElapsedMs(lead.created_at || lead.assigned_at);
    if (elapsedMs === null) return { text: '', color: '#6B7280', bgColor: '#F3F4F6' };
    
    // Handle negative elapsed time (future date - shouldn't happen but be safe)
    if (elapsedMs < 0) return { text: 'Just now', color: '#059669', bgColor: '#D1FAE5' };
    
    const elapsedMins = Math.floor(elapsedMs / (1000 * 60));
    const elapsedHours = Math.floor(elapsedMins / 60);
    const elapsedDays = Math.floor(elapsedHours / 24);
    
    // Within 15 minutes - very fresh
    if (elapsedMins < 15) {
      return { text: `${elapsedMins}m old`, color: '#059669', bgColor: '#D1FAE5' };
    }
    
    // Within 1 hour
    if (elapsedMins < 60) {
      return { text: `${elapsedMins}m old`, color: '#65A30D', bgColor: '#ECFCCB' };
    }
    
    // Within 24 hours
    if (elapsedHours < 24) {
      const remainMins = elapsedMins % 60;
      const text = remainMins > 0 ? `${elapsedHours}h ${remainMins}m old` : `${elapsedHours}h old`;
      if (elapsedHours >= 12) {
        return { text: `📅 ${text}`, color: '#D97706', bgColor: '#FEF3C7' };
      }
      return { text: `📅 ${text}`, color: '#65A30D', bgColor: '#ECFCCB' };
    }
    
    // Days old
    const remainHours = elapsedHours % 24;
    const text = remainHours > 0 ? `${elapsedDays}d ${remainHours}h old` : `${elapsedDays}d old`;
    if (elapsedDays >= 3) {
      return { text: `📅 ${text}`, color: '#DC2626', bgColor: '#FEE2E2' };
    }
    return { text: `📅 ${text}`, color: '#EA580C', bgColor: '#FFEDD5' };
  };
  
  // ============================================================================
  // FOLLOW-UP TAB: Today's follow-ups with badge, NO countdown timer
  // ============================================================================
  
  // Helper function to get follow-up info for leads in Follow-ups tab
  const getFollowUpInfo = (lead) => {
    if (!lead.follow_up_date) return null;
    
    try {
      // Dates are stored as naive IST - parse as local time (no UTC conversion)
      const followUpDate = new Date(lead.follow_up_date);
      if (isNaN(followUpDate.getTime())) return null;
      
      const now = new Date();
      
      // Get today's date boundaries in local time (IST)
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
      const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
      
      // Check if follow-up is today
      const isToday = followUpDate >= todayStart && followUpDate <= todayEnd;
      
      // Format the follow-up time for display
      let hours = followUpDate.getHours();
      const minutes = followUpDate.getMinutes().toString().padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12 || 12;
      const timeStr = `${hours}:${minutes} ${ampm}`;
      
      // Check if follow-up time has passed
      const isPast = followUpDate < now;
      
      if (isToday) {
        // Date-only overdue rule (Feb 2026): a follow-up scheduled for today
        // stays "Today" all day regardless of the clock — counsellors action
        // it on the day, not by the minute. We no longer differentiate by
        // time-of-day, so every today-dated lead gets the same green "Follow
        // Up Today" treatment and lands at the top of Follow-ups / RNR /
        // Future tabs.
        return {
          isToday: true,
          isPast: false,
          timeStr,
          badge: '📅 Follow Up Today',
          badgeColor: '#059669',
          badgeBg: '#D1FAE5',
          priority: 1
        };
      }
      
      // Check for tomorrow
      const tomorrowStart = new Date(todayStart.getTime() + 24 * 60 * 60 * 1000);
      const tomorrowEnd = new Date(todayEnd.getTime() + 24 * 60 * 60 * 1000);
      const isTomorrow = followUpDate >= tomorrowStart && followUpDate <= tomorrowEnd;
      
      if (isTomorrow) {
        return {
          isToday: false,
          isPast: false,
          timeStr,
          badge: '📆 Tomorrow',
          badgeColor: '#D97706',
          badgeBg: '#FEF3C7',
          priority: 2
        };
      }
      
      // Check if it's in the past (overdue) - should be in Overdue tab
      if (isPast) {
        const daysAgo = Math.floor((now - followUpDate) / (1000 * 60 * 60 * 24));
        return {
          isToday: false,
          isPast: true,
          timeStr,
          badge: `⚠️ ${daysAgo}d overdue`,
          badgeColor: '#DC2626',
          badgeBg: '#FEE2E2',
          priority: -1 // Show at bottom or move to overdue
        };
      }
      
      // Future follow-ups (more than tomorrow)
      const daysUntil = Math.ceil((followUpDate - now) / (1000 * 60 * 60 * 24));
      return {
        isToday: false,
        isPast: false,
        timeStr,
        badge: `📆 In ${daysUntil} days`,
        badgeColor: '#6B7280',
        badgeBg: '#F3F4F6',
        priority: 3
      };
    } catch {
      return null;
    }
  };
  
  // Sort leads for Follow-ups & Future tabs - Today's follow-ups first, then by time
  const sortedLeads = React.useMemo(() => {
    if (activeTab !== 'followups' && activeTab !== 'future') return leads;
    
    return [...leads].sort((a, b) => {
      const infoA = getFollowUpInfo(a);
      const infoB = getFollowUpInfo(b);
      
      // Leads without follow-up info go to the end
      if (!infoA && !infoB) return 0;
      if (!infoA) return 1;
      if (!infoB) return -1;
      
      // Sort by priority first (0 = passed today, 1 = today future, 2 = tomorrow, 3 = later)
      if (infoA.priority !== infoB.priority) {
        return infoA.priority - infoB.priority;
      }
      
      // Within same priority, sort by follow-up date
      const dateA = new Date(a.follow_up_date);
      const dateB = new Date(b.follow_up_date);
      return dateA - dateB;
    });
  }, [leads, activeTab]);
  
  // Reassign Dialog state
  const [reassignDialogOpen, setReassignDialogOpen] = useState(false);
  const [reassignLead, setReassignLead] = useState(null);
  const [reassignCounselorId, setReassignCounselorId] = useState('');
  const [reassignReason, setReassignReason] = useState('');
  const [reassigning, setReassigning] = useState(false);
  
  // Programs list for creatable select
  const [programs, setPrograms] = useState(['MBBS', 'BDS', 'Engineering', 'MBA', 'Nursing', 'Pharmacy']);
  const [intakes, setIntakes] = useState([]);

  // Get user from localStorage
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user?.role === 'admin' || user?.role === 'operating_head';
  const canAddProgram = ['admin', 'counselor', 'team_leader'].includes(user?.role);

  // Fetch programs and intakes from backend
  const fetchPrograms = async () => {
    try {
      const response = await apiClient.get('/constants');
      if (response.data.programs) {
        setPrograms(response.data.programs);
      }
      if (response.data.intakes) {
        setIntakes(response.data.intakes);
      }
    } catch (error) {
      console.error('Error fetching programs:', error);
    }
  };

  // Add new program to backend
  const handleAddProgram = async (programName) => {
    try {
      await apiClient.post('/constants/programs', { program: programName });
      await fetchPrograms();
      toast.success(`Program "${programName}" added successfully`);
    } catch (error) {
      console.error('Error adding program:', error);
      toast.error('Failed to add program');
    }
  };

  // Fetch programs on mount
  useEffect(() => {
    fetchPrograms();
  }, []);

  // Fetch dynamic filter options (countries and programs from managed list)
  const fetchDynamicFilters = useCallback(async () => {
    setLoadingFilters(true);
    try {
      const [countriesRes, programsRes] = await Promise.all([
        apiClient.get('/students/distinct/countries'),
        apiClient.get('/constants')
      ]);
      setFilterCountries(countriesRes.data.countries || []);
      setFilterPrograms(programsRes.data.programs || []);
    } catch (error) {
      console.error('Error fetching dynamic filters:', error);
    } finally {
      setLoadingFilters(false);
    }
  }, []);

  // Fetch dynamic filters on mount
  useEffect(() => {
    fetchDynamicFilters();
  }, [fetchDynamicFilters]);

  // Fetch leads based on tab with server-side pagination
  const fetchLeads = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        tab: activeTab,
        page: page.toString(),
        page_size: '50'
      });
      
      if (search) params.append('search', search);
      if (showProspective) params.append('is_prospective', 'true');
      if (countryFilter !== 'all') params.append('desired_country', countryFilter);
      if (programFilter !== 'all') params.append('program_type', programFilter);
      if (counsellorFilter !== 'all') params.append('assigned_counselor_id', counsellorFilter);
      
      // For Follow-ups tab, use followup date filter; for others use created date filter
      if (activeTab === 'followups') {
        if (followupDateFilter.startDate) params.append('followup_start_date', followupDateFilter.startDate);
        if (followupDateFilter.endDate) params.append('followup_end_date', followupDateFilter.endDate);
      } else {
        if (dateFilter.startDate) params.append('start_date', dateFilter.startDate);
        if (dateFilter.endDate) params.append('end_date', dateFilter.endDate);
      }

      // Overdue sub-type filter (server-side, Feb 2026). Drives the filter
      // chips so they query the full overdue corpus, not just the current page.
      if (activeTab === 'overdue' && overdueTypeFilter && overdueTypeFilter !== 'all') {
        params.append('overdue_type', overdueTypeFilter);
      }

      const response = await apiClient.get(`/leads/activity-view?${params}`);
      let fetchedLeads = response.data.leads || [];
      
      // RNR tab: Sort so today's upcoming follow-ups appear at the top
      if (activeTab === 'rnr') {
        const now = new Date();
        const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
        const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
        
        const parseDate = (dateStr) => {
          if (!dateStr) return null;
          const d = new Date(dateStr);
          return isNaN(d.getTime()) ? null : d;
        };

        // Use the next scheduled follow-up — prefer rnr_follow_up_date, fall back to follow_up_date
        const effectiveFollowUp = (lead) => parseDate(lead.rnr_follow_up_date) || parseDate(lead.follow_up_date);
        
        fetchedLeads.sort((a, b) => {
          const aDate = effectiveFollowUp(a);
          const bDate = effectiveFollowUp(b);
          const aIsToday = aDate && aDate >= todayStart && aDate <= todayEnd;
          const bIsToday = bDate && bDate >= todayStart && bDate <= todayEnd;
          
          // Priority: Today's upcoming first, then future dates, then no date
          if (aIsToday && !bIsToday) return -1;
          if (!aIsToday && bIsToday) return 1;
          if (aIsToday && bIsToday) return aDate - bDate; // Earlier time first
          if (aDate && !bDate) return -1;  // Has date before no date
          if (!aDate && bDate) return 1;
          if (aDate && bDate) return aDate - bDate; // Ascending
          return 0;
        });
      }
      
      setLeads(fetchedLeads);
      setTabCounts(response.data.counts || tabCounts);
      setPagination(response.data.pagination || {
        page: 1, page_size: 50, total_count: 0, total_pages: 0, has_next: false, has_prev: false
      });
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [activeTab, search, showProspective, countryFilter, programFilter, counsellorFilter, dateFilter, followupDateFilter, overdueTypeFilter]);

  // Pagination handlers
  const goToPage = (page) => {
    if (page >= 1 && page <= pagination.total_pages) {
      fetchLeads(page);
    }
  };

  const goToNextPage = () => {
    if (pagination.has_next) {
      goToPage(pagination.page + 1);
    }
  };

  const goToPrevPage = () => {
    if (pagination.has_prev) {
      goToPage(pagination.page - 1);
    }
  };

  // Fetch counsellors — used for filter + reassign dropdown. Uses the
  // dedicated `/users/counsellors` endpoint (open to all staff), so it works
  // for Operating Head too — they don't have access to `/users` admin list.
  const fetchCounsellors = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const response = await apiClient.get('/users/counsellors');
      const list = Array.isArray(response.data)
        ? response.data
        : (response.data?.counsellors || response.data?.counselors || []);
      // Keep only active entries with counselor / team_leader roles
      const counsellorList = list.filter(
        u => ((u.role === 'counselor' || u.role === 'team_leader')
              || (u.role === 'operating_head' && u.also_counselor)) && u.is_active !== false
      );
      setCounsellors(counsellorList);
    } catch (error) {
      console.error('Error fetching counsellors:', error);
    }
  }, [isAdmin]);

  // Helper function to get today's date range (local time, no UTC conversion)
  const getTodayDateRange = () => {
    const today = new Date();
    const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0);
    const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);
    const pad = (n) => String(n).padStart(2, '0');
    const formatLocal = (d) => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    return {
      startDate: formatLocal(startOfDay),
      endDate: formatLocal(endOfDay),
      preset: 'today'
    };
  };

  // Reset to page 1 when tab or filters change
  useEffect(() => {
    fetchLeads(1);
  }, [activeTab, showProspective, countryFilter, programFilter, counsellorFilter, dateFilter, followupDateFilter, overdueTypeFilter]);

  // Auto-refresh the current page every 45s (replaces the manual refresh button)
  useEffect(() => {
    const id = setInterval(() => { fetchLeads(pagination.page); }, 45000);
    return () => clearInterval(id);
  }, [fetchLeads, pagination.page]);

  // Reset overdue sub-filter when leaving the Overdue tab so it doesn't
  // leak into a future return visit and silently filter results.
  useEffect(() => {
    if (activeTab !== 'overdue' && overdueTypeFilter !== 'all') {
      setOverdueTypeFilter('all');
    }
  }, [activeTab, overdueTypeFilter]);

  useEffect(() => {
    fetchCounsellors();
  }, []);

  // Fetch Power Hours status and available leads (for counselors and admins)
  const fetchPowerHoursData = useCallback(async () => {
    if (user?.role !== 'counselor' && user?.role !== 'admin' && user?.role !== 'operating_head') return;
    
    try {
      const statusRes = await apiClient.get('/power-hours/status');
      setPowerHoursStatus(statusRes.data);
      
      // Fetch available leads if Power Hours is active OR pool mode
      if (statusRes.data?.is_active || statusRes.data?.pool_mode_active) {
        try {
          const leadsRes = await apiClient.get('/power-hours/available-leads');
          setPowerHoursLeads(leadsRes.data?.leads || []);
        } catch {
          setPowerHoursLeads([]);
        }
      } else {
        setPowerHoursLeads([]);
      }
    } catch (error) {
      console.error('Error fetching Power Hours status:', error);
      setPowerHoursStatus(null);
      setPowerHoursLeads([]);
    }
  }, [user?.role]);

  // Fetch Power Hours data on mount and when tab changes
  useEffect(() => {
    fetchPowerHoursData();
  }, [fetchPowerHoursData]);
  
  // Auto-refresh pool data every 30s so admin/counsellor see new leads coming in
  useEffect(() => {
    if (user?.role !== 'counselor' && user?.role !== 'admin' && user?.role !== 'operating_head') return;
    const t = setInterval(() => { fetchPowerHoursData(); }, 30000);
    return () => clearInterval(t);
  }, [fetchPowerHoursData, user?.role]);

  // Claim Power Hours lead
  const handleClaimPowerHoursLead = async (leadId) => {
    setClaimingLead(leadId);
    try {
      const response = await apiClient.post(`/power-hours/claim/${leadId}`);
      toast.success(response.data?.message || 'Lead claimed successfully! 🎉');
      // Refresh Power Hours data and leads
      fetchPowerHoursData();
      fetchLeads(1);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to claim lead');
    } finally {
      setClaimingLead(null);
    }
  };

  // Debounced search - reset to page 1
  useEffect(() => {
    const timer = setTimeout(() => {
      if (search !== undefined) fetchLeads(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Export leads to Excel
  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ tab: activeTab });
      
      if (search) params.append('search', search);
      if (showProspective) params.append('is_prospective', 'true');
      if (countryFilter !== 'all') params.append('desired_country', countryFilter);
      if (programFilter !== 'all') params.append('program_type', programFilter);
      if (counsellorFilter !== 'all') params.append('assigned_counselor_id', counsellorFilter);
      if (dateFilter.startDate) params.append('start_date', dateFilter.startDate);
      if (dateFilter.endDate) params.append('end_date', dateFilter.endDate);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads/export-excel?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Export failed');
      }
      
      // Get the blob and create download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `leads_${activeTab}_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(`Exported ${pagination.total_count} leads to Excel`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export leads');
    } finally {
      setExporting(false);
    }
  };

  // ---- Clear Bad Leads (admin): preview → download → delete ----
  const _clearParams = () => {
    const p = new URLSearchParams({ date_field: clearForm.date_field });
    if (clearForm.from_date) p.append('from_date', clearForm.from_date);
    if (clearForm.to_date) p.append('to_date', clearForm.to_date);
    return p;
  };
  const previewClearBad = async () => {
    setClearBusy(true); setClearCount(null); setClearDownloaded(false);
    try {
      const res = await apiClient.get(`/admin/bad-leads/preview?${_clearParams()}`);
      setClearCount(res.data.count);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to fetch count');
    } finally { setClearBusy(false); }
  };
  const downloadClearBad = async () => {
    setClearBusy(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/bad-leads/export?${_clearParams()}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bad_leads_${clearForm.from_date || 'all'}_${clearForm.to_date || 'all'}.xlsx`;
      document.body.appendChild(a); a.click();
      window.URL.revokeObjectURL(url); document.body.removeChild(a);
      setClearDownloaded(true);
      toast.success('Excel downloaded — delete is now enabled');
    } catch (e) {
      toast.error('Failed to download Excel');
    } finally { setClearBusy(false); }
  };
  const deleteClearBad = async () => {
    setClearBusy(true);
    try {
      const res = await apiClient.post('/admin/bad-leads/delete', {
        date_field: clearForm.date_field,
        from_date: clearForm.from_date || null,
        to_date: clearForm.to_date || null,
        confirm_count: clearCount,
      });
      toast.success(`Deleted ${res.data.deleted} bad leads`);
      setShowClearBad(false);
      setClearForm({ date_field: 'created_at', from_date: '', to_date: '' });
      setClearCount(null); setClearDownloaded(false);
      fetchLeads(1);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    } finally { setClearBusy(false); }
  };

  // Add new lead
  const handleAddLead = async () => {
    if (!newLead.full_name || !newLead.phone) {
      toast.error('Name and phone are required');
      return;
    }
    
    setAddingLead(true);
    try {
      const response = await apiClient.post('/students', newLead);
      toast.success(`Lead created! ID: ${response.data.student_id}`);
      setAddDialogOpen(false);
      setNewLead({
        full_name: '', phone: '', email: '', lead_source: 'manual',
        desired_country: '', desired_course: '', eligible_intake: '', notes: '',
        assign_to_counselor_id: null
      });
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create lead');
    } finally {
      setAddingLead(false);
    }
  };

  // Open student profile
  const openStudentProfile = (lead) => {
    navigate(`/student/${lead.id}`);
  };

  // Call button handler - Records call initiation time for First Strike tracking
  const handleCall = async (e, lead) => {
    e.stopPropagation();
    
    // Record call initiation timestamp for First Strike calculation
    // This captures the moment counselor clicks to call, not when they mark it connected
    try {
      await apiClient.post(`/students/${lead.id}/call-initiated`);
    } catch (error) {
      // Don't block the call if tracking fails
      console.error('Failed to record call initiation:', error);
    }
    
    // Open the phone dialer
    window.open(`tel:${lead.phone}`, '_self');
  };

  // WhatsApp button handler
  const handleWhatsApp = (e, lead) => {
    e.stopPropagation();
    const phone = lead.phone.replace(/[^0-9]/g, '');
    const formattedPhone = phone.startsWith('91') ? phone : `91${phone}`;
    window.open(`https://wa.me/${formattedPhone}`, '_blank');
  };

  // Toggle prospective status
  const handleToggleProspective = async (e, lead) => {
    e.stopPropagation();
    try {
      const newValue = !lead.is_prospective;
      await apiClient.put(`/students/${lead.id}`, {
        is_prospective: newValue
      });
      toast.success(newValue ? 'Marked as Prospective ⭐' : 'Removed Prospective flag');
      fetchLeads(); // Refresh the leads list
    } catch (error) {
      toast.error('Failed to update prospective status');
    }
  };

  // Open Reassign Dialog (Admin only)
  const openReassignDialog = (e, lead) => {
    e.stopPropagation();
    setReassignLead(lead);
    setReassignCounselorId('');
    setReassignReason('');
    setReassignDialogOpen(true);
  };

  // Submit reassignment
  const handleSubmitReassign = async () => {
    if (!reassignCounselorId) {
      toast.error('Please select a counselor');
      return;
    }
    
    setReassigning(true);
    try {
      await apiClient.put(`/students/${reassignLead.id}/reassign`, {
        new_counselor_id: reassignCounselorId,
        reason: reassignReason || ''
      });
      
      const selectedCounselor = counsellors.find(c => c.id === reassignCounselorId);
      toast.success(`Lead reassigned to ${selectedCounselor?.full_name || 'counselor'}`);
      
      setReassignDialogOpen(false);
      setReassignLead(null);
      fetchLeads(); // Refresh the leads list
    } catch (error) {
      console.error('Error reassigning lead:', error);
      toast.error(error.response?.data?.detail || 'Failed to reassign lead');
    } finally {
      setReassigning(false);
    }
  };

  // Helper function to determine overdue type
  const getOverdueType = (lead) => {
    // Persistent flag from the never-connected auto-reschedule cron wins —
    // a forwarded legacy lead keeps its "Never Contacted" identity even after
    // we stamp a future follow_up_date on it.
    if (lead.never_connected === true) {
      return 'never_contacted';
    }
    // Missed Future follow-up: lead_status is future and has missed follow_up_date
    const FUTURE_STATUSES = ['future_prospect', 'future_prospective', 'future'];
    if (FUTURE_STATUSES.includes(lead.lead_status)) {
      return 'missed_future';
    }
    // Never contacted: No calls at all
    if ((lead.total_calls || 0) === 0) {
      return 'never_contacted';
    }
    // Missed RNR follow-up: has rnr_consecutive > 0 and rnr_follow_up_date in the past
    if ((lead.rnr_consecutive || 0) > 0 && lead.rnr_follow_up_date) {
      return 'missed_rnr';
    }
    // If has follow_up_date that's in the past, it's a missed follow-up
    if (lead.follow_up_date) {
      return 'missed_followup';
    }
    // Default
    return 'missed_followup';
  };

  // Open Update Call Response dialog (shared guided flow manages its own state)
  const openCallResponseDialog = (e, lead) => {
    e.stopPropagation();
    setSelectedLead(lead);
    setCallDialogOpen(true);
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Count active filters for badge
  const getActiveFilterCount = () => {
    let count = 0;
    if (showProspective) count++;
    if (countryFilter !== 'all') count++;
    if (programFilter !== 'all') count++;
    if (counsellorFilter !== 'all') count++;
    // For Follow-ups tab, count followup date filter; for others, count date filter
    if (activeTab === 'followups') {
      if (followupDateFilter.preset !== 'all') count++; // "All Time" is now the default for Follow-ups
    } else {
      if (dateFilter.preset !== 'all') count++;
    }
    return count;
  };

  // Clear all filters
  const clearAllFilters = () => {
    setShowProspective(false);
    setCountryFilter('all');
    setProgramFilter('all');
    setCounsellorFilter('all');
    setDateFilter({ startDate: null, endDate: null, preset: 'all' });
    setFollowupDateFilter({ startDate: null, endDate: null, preset: 'all' });
  };

  // Handle date filter change
  const handleDateFilterChange = (newDateFilter) => {
    if (activeTab === 'followups') {
      setFollowupDateFilter(newDateFilter);
    } else {
      setDateFilter(newDateFilter);
    }
  };

  return (
    <div className="min-h-screen p-2 sm:p-4" style={{ backgroundColor: COLORS.background }}>
      {/* Sticky wrapper: keeps Tabs + Filters pinned so switching tabs on phones
          doesn't require scrolling back up. Offset sits just below the app header. */}
      <div className="sticky top-14 sm:top-16 z-20">
      {/* Compact Header with Tabs */}
      <div className="rounded-xl" style={{ backgroundColor: COLORS.primary }}>
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-2 sm:py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: COLORS.accent }}>
                <Users className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <h1 className="text-base sm:text-lg font-semibold text-white">Leads</h1>
            </div>
            <Button 
              onClick={() => setAddDialogOpen(true)}
              size="sm"
              className="h-7 sm:h-8 px-2 sm:px-3 text-[10px] sm:text-xs"
              style={{ backgroundColor: COLORS.accent }}
              data-testid="add-lead-btn"
            >
              <Plus className="w-3 h-3 sm:w-3.5 sm:h-3.5 sm:mr-1" />
              <span className="hidden sm:inline">Add Lead</span>
              <span className="sm:hidden">Add</span>
            </Button>
          </div>
        </div>
        
        {/* Tabs Row - Mobile optimized */}
        <div style={{ backgroundColor: 'rgba(255,255,255,0.1)' }} className="rounded-b-xl">
          <div className="max-w-7xl mx-auto px-2 sm:px-4">
            <div className="flex gap-1 py-2 overflow-x-auto scrollbar-hide">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  data-testid={`tab-${tab.id}`}
                  className={`
                    flex items-center shrink-0 px-2 sm:px-3 py-1.5 rounded-lg text-[10px] sm:text-xs font-medium whitespace-nowrap
                    transition-all duration-200
                    ${activeTab === tab.id 
                      ? 'bg-white text-gray-900 shadow-sm' 
                      : 'text-white/80 hover:bg-white/10'}
                  `}
                >
                  <tab.icon className="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-1" />
                  <span>{tab.label}</span>
                  <span 
                    className={`ml-1 min-w-[18px] px-1 sm:px-1.5 py-0.5 rounded-full text-[9px] sm:text-[10px] font-semibold text-center
                      ${activeTab === tab.id ? 'bg-gray-100' : 'bg-white/20'}
                    `}
                  >
                    {tabCounts[tab.id] || 0}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
      </div>

      {/* Filters Row - Redesigned for Mobile */}
      <div className="border-b" style={{ backgroundColor: COLORS.cardBg, borderColor: COLORS.border }}>
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-2 flex flex-col sm:flex-row sm:items-center sm:gap-3 space-y-2 sm:space-y-0">
          {/* Row 1: Search + Mobile Actions */}
          <div className="flex items-center gap-2 sm:shrink-0">
            {/* Search - Always visible */}
            <div className="relative flex-1 min-w-[180px] sm:max-w-[220px]">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <Input
                placeholder="Search name, phone..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-8 pl-8 pr-2 text-xs"
                style={{ borderColor: COLORS.border }}
                data-testid="search-input"
              />
            </div>
            
            {/* Mobile: Filter Button + Actions */}
            <div className="flex sm:hidden items-center gap-1.5">
              {/* Filter Button - Opens Bottom Sheet */}
              <Sheet open={filterSheetOpen} onOpenChange={setFilterSheetOpen} modal={false}>
                <SheetTrigger asChild>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="h-8 px-2.5 relative"
                    style={{ borderColor: getActiveFilterCount() > 0 ? COLORS.accent : COLORS.border }}
                    data-testid="mobile-filter-btn"
                  >
                    <SlidersHorizontal className="w-3.5 h-3.5" style={{ color: getActiveFilterCount() > 0 ? COLORS.accent : undefined }} />
                    {getActiveFilterCount() > 0 && (
                      <span 
                        className="absolute -top-1 -right-1 w-4 h-4 rounded-full text-[9px] font-bold flex items-center justify-center text-white"
                        style={{ backgroundColor: COLORS.accent }}
                      >
                        {getActiveFilterCount()}
                      </span>
                    )}
                  </Button>
                </SheetTrigger>
                <SheetContent side="bottom" className="h-auto max-h-[70vh] rounded-t-2xl">
                  <SheetHeader className="pb-4">
                    <SheetTitle className="flex items-center gap-2 text-base" style={{ color: COLORS.primary }}>
                      <SlidersHorizontal className="w-4 h-4" style={{ color: COLORS.accent }} />
                      Filter Leads
                    </SheetTitle>
                    <SheetDescription className="text-xs">
                      {getActiveFilterCount() > 0 
                        ? `${getActiveFilterCount()} filter${getActiveFilterCount() > 1 ? 's' : ''} active` 
                        : 'No filters applied'}
                    </SheetDescription>
                  </SheetHeader>
                  
                  {/* Mobile Filter Content - Inline */}
                  <div className="space-y-4">
                    {/* Prospective Toggle */}
                    <div>
                      <Label className="text-xs mb-1.5 block text-gray-500">Prospective Only</Label>
                      <button
                        onClick={() => setShowProspective(!showProspective)}
                        data-testid="prospective-filter-mobile"
                        className={`
                          flex items-center gap-2 w-full justify-between p-3 rounded-lg text-xs font-medium border transition-all whitespace-nowrap
                          ${showProspective 
                            ? 'border-yellow-400 bg-yellow-50 text-yellow-700' 
                            : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'}
                        `}
                      >
                        <span className="flex items-center gap-1.5">
                          <Star className={`w-3.5 h-3.5 ${showProspective ? 'fill-yellow-500 text-yellow-500' : ''}`} />
                          Prospective Leads Only
                        </span>
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center ${showProspective ? 'bg-yellow-500' : 'bg-gray-200'}`}>
                          {showProspective && <Check className="w-3 h-3 text-white" />}
                        </div>
                      </button>
                    </div>
                    
                    {/* Counsellor Filter (Admin only) */}
                    {isAdmin && counsellors.length > 0 && (
                      <div>
                        <Label className="text-xs mb-1.5 block text-gray-500">Counsellor</Label>
                        <select
                          value={counsellorFilter}
                          onChange={(e) => setCounsellorFilter(e.target.value)}
                          data-testid="counsellor-filter-select-mobile"
                          className="w-full h-10 text-sm bg-white border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer"
                        >
                          <option value="all">All Counsellors</option>
                          {counsellors.map(c => (
                            <option key={c.id} value={c.id}>{c.full_name}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                  
                  <SheetFooter className="flex flex-row gap-2 pt-4 mt-4 border-t">
                    {getActiveFilterCount() > 0 && (
                      <Button 
                        variant="outline" 
                        className="flex-1 h-10"
                        onClick={clearAllFilters}
                        data-testid="clear-filters-btn"
                      >
                        <X className="w-4 h-4 mr-1.5" />
                        Clear All
                      </Button>
                    )}
                    <Button 
                      className="flex-1 h-10"
                      style={{ backgroundColor: COLORS.accent }}
                      onClick={() => setFilterSheetOpen(false)}
                      data-testid="apply-filters-btn"
                    >
                      <Check className="w-4 h-4 mr-1.5" />
                      Apply Filters
                    </Button>
                  </SheetFooter>
                </SheetContent>
              </Sheet>
              
              {isAdmin && activeTab === 'bad' && (
                <Button variant="outline" size="sm" onClick={() => setShowClearBad(true)}
                  className="h-8 w-8 p-0 text-red-600 border-red-200" data-testid="clear-bad-leads-btn-mobile">
                  <AlertTriangle className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
            
            {/* Desktop: Clear Bad (admin) — export/refresh removed; list auto-refreshes */}
            {isAdmin && activeTab === 'bad' && (
              <div className="hidden sm:flex items-center gap-1 ml-auto shrink-0">
                <Button variant="outline" size="sm" onClick={() => setShowClearBad(true)}
                  className="h-8 px-2.5 text-xs whitespace-nowrap text-red-600 border-red-200 hover:bg-red-50"
                  data-testid="clear-bad-leads-btn">
                  <AlertTriangle className="w-3.5 h-3.5 mr-1" /> Clear Bad
                </Button>
              </div>
            )}
          </div>

          {/* Clear Bad Leads Dialog (admin) */}
          <Dialog open={showClearBad} onOpenChange={(o) => { setShowClearBad(o); if (!o) { setClearCount(null); setClearDownloaded(false); } }}>
            <DialogContent className="max-w-lg" data-testid="clear-bad-leads-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-red-500" /> Clear Bad Leads</DialogTitle>
                <DialogDescription>Download a full Excel backup first, then permanently delete the matching bad leads to free up space. This cannot be undone.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="text-xs">Date applies to</Label>
                  <Select value={clearForm.date_field} onValueChange={(v) => { setClearForm({ ...clearForm, date_field: v }); setClearCount(null); setClearDownloaded(false); }}>
                    <SelectTrigger data-testid="clear-date-field"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="created_at">Lead created date</SelectItem>
                      <SelectItem value="updated_at">Became bad / last updated</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">From</Label><Input type="date" value={clearForm.from_date} onChange={(e) => { setClearForm({ ...clearForm, from_date: e.target.value }); setClearCount(null); setClearDownloaded(false); }} data-testid="clear-from-date" /></div>
                  <div><Label className="text-xs">To</Label><Input type="date" value={clearForm.to_date} onChange={(e) => { setClearForm({ ...clearForm, to_date: e.target.value }); setClearCount(null); setClearDownloaded(false); }} data-testid="clear-to-date" /></div>
                </div>
                <p className="text-[11px] text-gray-500">Leave dates empty to target ALL bad leads. Uses the same set as the Bad tab.</p>
                <Button variant="outline" size="sm" onClick={previewClearBad} disabled={clearBusy} data-testid="clear-preview-btn" className="w-full">
                  {clearBusy ? 'Checking…' : 'Preview count'}
                </Button>
                {clearCount !== null && (
                  <div className="rounded-lg border p-3 bg-amber-50 border-amber-200 text-sm" data-testid="clear-count-box">
                    <b className="text-amber-800">{clearCount.toLocaleString()}</b> bad leads match this filter.
                  </div>
                )}
                {clearCount !== null && clearCount > 0 && (
                  <div className="flex flex-col gap-2">
                    <Button variant="outline" onClick={downloadClearBad} disabled={clearBusy} data-testid="clear-download-btn" className="border-indigo-200 text-indigo-700 hover:bg-indigo-50">
                      <Download className="w-4 h-4 mr-1" /> {clearDownloaded ? 'Downloaded ✓ — Re-download (optional)' : `Download Excel backup (optional)`}
                    </Button>
                    <Button onClick={deleteClearBad} disabled={clearBusy} data-testid="clear-delete-btn" className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50">
                      <XCircle className="w-4 h-4 mr-1" /> {clearBusy ? 'Deleting…' : `Permanently delete ${clearCount.toLocaleString()} leads`}
                    </Button>
                    <p className="text-[11px] text-gray-500 text-center">Downloading a backup is optional — delete works without it. The current matching leads are removed at delete time.</p>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>

          {/* Row 2: Desktop Inline Filter Chips */}
          <div className="hidden sm:flex items-center gap-2 flex-wrap">
              {/* Prospective Toggle */}
              <button
                onClick={() => setShowProspective(!showProspective)}
                data-testid="prospective-filter-desktop"
                className={`
                  flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-medium border transition-all whitespace-nowrap shrink-0
                  ${showProspective 
                    ? 'border-yellow-400 bg-yellow-50 text-yellow-700' 
                    : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'}
                `}
              >
                <Star className={`w-3.5 h-3.5 ${showProspective ? 'fill-yellow-500 text-yellow-500' : ''}`} />
                Prospective
              </button>
              
              {/* Counsellor Filter (Admin only) */}
              {isAdmin && (
                <Select value={counsellorFilter} onValueChange={setCounsellorFilter}>
                  <SelectTrigger className="h-8 w-auto min-w-[130px] text-xs" style={{ borderColor: COLORS.border }} data-testid="counsellor-filter-select">
                    <SelectValue placeholder="All Counsellors" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Counsellors</SelectItem>
                    {counsellors.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              
              {/* Clear filters button (desktop) */}
              {getActiveFilterCount() > 0 && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={clearAllFilters}
                  className="h-8 px-2 text-xs text-gray-500 hover:text-gray-700 shrink-0"
                  data-testid="desktop-clear-filters-btn"
                >
                  <X className="w-3 h-3 mr-1" />
                  Clear
                </Button>
              )}
            </div>
          
          {/* Active Filters Summary (Mobile) - Show below search when filters are active */}
          {getActiveFilterCount() > 0 && (
            <div className="flex sm:hidden items-center gap-1.5 mt-2 flex-wrap">
              {showProspective && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-yellow-50 text-yellow-700 border border-yellow-200">
                  <Star className="w-2.5 h-2.5 fill-yellow-500" />
                  Prospective
                </span>
              )}
              {countryFilter !== 'all' && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-50 text-blue-700 border border-blue-200">
                  {countryFilter}
                </span>
              )}
              {programFilter !== 'all' && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-50 text-green-700 border border-green-200">
                  {programFilter}
                </span>
              )}
              {counsellorFilter !== 'all' && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-purple-50 text-purple-700 border border-purple-200">
                  {counsellors.find(c => c.id === counsellorFilter)?.full_name || 'Counsellor'}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Power Hours / Pool Mode Section - For counselors (claim) and admins (view) */}
      {(user?.role === 'counselor' || user?.role === 'admin' || user?.role === 'operating_head') && (powerHoursStatus?.is_active || powerHoursStatus?.pool_mode_active) && activeTab === 'new' && (
        <div className="max-w-7xl mx-auto px-2 sm:px-4 pb-4">
          {/* Banner */}
          <div 
            className="rounded-xl p-4 md:p-5 shadow-lg mb-4"
            style={{ 
              background: powerHoursStatus?.is_active 
                ? 'linear-gradient(135deg, #F59E0B 0%, #EA580C 100%)'
                : 'linear-gradient(135deg, #3B82F6 0%, #6366F1 100%)',
              border: powerHoursStatus?.is_active ? '2px solid #FBBF24' : '2px solid #818CF8'
            }}
            data-testid="pool-banner"
          >
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <i className={`fas ${powerHoursStatus?.is_active ? 'fa-bolt animate-pulse' : 'fa-users'} text-2xl text-white`}></i>
                </div>
                <div>
                  <h2 className="text-lg md:text-xl font-bold text-white flex items-center gap-2">
                    {powerHoursStatus?.is_active ? 'Power Hours Active!' : (powerHoursStatus?.lead_assignment_mode === 'hybrid' ? 'Hybrid Pool Active' : 'Leads Pool Active')}
                  </h2>
                  <p className="text-sm text-white/90">
                    {powerHoursStatus?.is_active 
                      ? <>{powerHoursStatus.day_name} {powerHoursStatus.is_sunday ? '(Full Day)' : '(Evening)'}: {powerHoursStatus.start_time} - {powerHoursStatus.end_time} IST | <span className="font-bold ml-1">+{powerHoursStatus.bonus_percentage}% Bonus</span></>
                      : powerHoursStatus?.lead_assignment_mode === 'hybrid'
                        ? (user?.role === 'counselor'
                            ? `Claim within ${powerHoursStatus?.pool_claim_window_minutes || 15} min — unclaimed leads auto-assigned via round-robin`
                            : `Hybrid mode — leads stay in pool ${powerHoursStatus?.pool_claim_window_minutes || 15} min, then auto-assigned. ${powerHoursLeads.length} waiting`)
                        : (user?.role === 'counselor' 
                            ? 'Auto-assignment is OFF — Claim leads below (normal incentive applies)'
                            : `Auto-assignment is OFF — ${powerHoursLeads.length} lead${powerHoursLeads.length !== 1 ? 's' : ''} waiting for counselors to claim`)}
                  </p>
                </div>
              </div>
              {powerHoursLeads.length > 0 && (
                <span className={`inline-flex items-center gap-2 px-4 py-2 font-bold rounded-full ${
                  powerHoursStatus?.is_active ? 'bg-white text-orange-600 animate-bounce' : 'bg-white text-blue-600'
                }`}>
                  {powerHoursStatus?.is_active ? '' : ''} {powerHoursLeads.length} Lead{powerHoursLeads.length > 1 ? 's' : ''} {user?.role === 'counselor' ? 'to Claim!' : 'in Pool'}
                </span>
              )}
              {(user?.role === 'admin' || user?.role === 'operating_head') && !powerHoursStatus?.is_active && (
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    onClick={async () => {
                      if (autoAssigningAll) return;
                      if (!window.confirm(`Force auto-assign ALL ${powerHoursLeads.length} pool lead${powerHoursLeads.length !== 1 ? 's' : ''} via round-robin to available counsellors right now?`)) return;
                      setAutoAssigningAll(true);
                      try {
                        const res = await apiClient.post('/leads/pool/auto-assign-now');
                        const d = res.data || {};
                        const dist = (d.distribution || []).filter(x => x.count > 0).map(x => `${x.name}: ${x.count}`).join(' · ');
                        toast.success(`${d.assigned_count || 0} leads auto-assigned${dist ? ` (${dist})` : ''}${d.skipped_no_counsellor ? ` · ${d.skipped_no_counsellor} held back` : ''}`, { duration: 8000 });
                        fetchLeads();
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Auto-assign failed');
                      } finally {
                        setAutoAssigningAll(false);
                      }
                    }}
                    disabled={autoAssigningAll || powerHoursLeads.length === 0}
                    className="inline-flex items-center gap-1.5 px-3 py-2 bg-white text-indigo-700 hover:bg-indigo-50 disabled:opacity-50 text-xs font-bold rounded-lg shadow-sm transition-colors"
                    data-testid="auto-assign-all-pool-btn"
                    title="Round-robin assign all pool leads to available counsellors immediately"
                  >
                    <i className={`fas ${autoAssigningAll ? 'fa-spinner fa-spin' : 'fa-bolt'}`}></i>
                    <span>{autoAssigningAll ? 'Assigning…' : 'Auto-Assign All Now'}</span>
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        const res = await apiClient.post('/power-hours/pool/cleanup-stale');
                        const d = res.data || {};
                        const total = (d.power_hours_pool_marked_claimed || 0) + (d.stale_wati_leads_cleared || 0);
                        if (total === 0) {
                          toast.success('Pool is clean — no stale entries found.');
                        } else {
                          toast.success(`Cleared ${total} stale pool entries (${d.power_hours_pool_marked_claimed || 0} pool + ${d.stale_wati_leads_cleared || 0} WATI).`);
                        }
                        fetchLeads();
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Cleanup failed');
                      }
                    }}
                    className="inline-flex items-center gap-1.5 px-3 py-2 bg-white/15 hover:bg-white/25 text-white text-xs font-semibold rounded-lg border border-white/30 transition-colors"
                    data-testid="cleanup-stale-pool-btn"
                    title="Remove pool entries whose underlying leads are already assigned to a counsellor"
                  >
                    <i className="fas fa-broom"></i>
                    <span>Clean Stale Pool</span>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Available Leads */}
          {powerHoursLeads.length > 0 && (
            <div className={`rounded-xl border-2 p-4 mb-4 ${
              powerHoursStatus?.is_active 
                ? 'bg-gradient-to-r from-yellow-50 to-orange-50 border-yellow-400'
                : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-400'
            }`} data-testid="pool-leads-section">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-base font-bold text-gray-800 flex items-center gap-2">
                  <i className={`fas ${powerHoursStatus?.is_active ? 'fa-fire text-orange-500' : user?.role === 'counselor' ? 'fa-hand-pointer text-blue-500' : 'fa-eye text-blue-500'}`}></i>
                  {user?.role === 'counselor' ? 'Claim Now - First Come First Serve!' : 'Unclaimed Pool Leads'}
                </h3>
                <span className={`text-sm ${powerHoursStatus?.is_active ? 'text-orange-700' : 'text-blue-700'}`}>
                  {powerHoursStatus?.is_active 
                    ? <>Claim & connect to earn <strong>+{powerHoursStatus.bonus_percentage}%</strong> bonus</>
                    : user?.role === 'counselor' ? 'Normal incentive applies' : 'Visible to counselors for claiming'}
                </span>
              </div>
              
              {user?.role === 'counselor' && powerHoursLeads.length > 1 && (
                <div className="mb-3 px-3 py-2 bg-green-50 border border-green-200 rounded-lg text-xs text-green-800">
                  <i className="fas fa-star mr-1.5 text-green-500"></i>
                  <strong>Tip:</strong> Latest leads are shown first — claim the top one for the freshest opportunity!
                </div>
              )}
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {powerHoursLeads.map((lead, index) => (
                  <div 
                    key={lead.id || index}
                    className={`flex flex-col p-4 bg-white rounded-lg border hover:shadow-md transition-all ${
                      powerHoursStatus?.is_active ? 'border-yellow-300' : 'border-blue-300'
                    } ${index === 0 && user?.role === 'counselor' && powerHoursLeads.length > 1 ? 'ring-2 ring-green-400 ring-offset-1' : ''}`}
                    data-testid={`pool-lead-${lead.lead_id}`}
                  >
                    {index === 0 && user?.role === 'counselor' && powerHoursLeads.length > 1 && (
                      <div className="text-[10px] font-bold text-green-700 bg-green-50 px-2 py-0.5 rounded mb-2 w-fit">
                        <i className="fas fa-arrow-up mr-1"></i>LATEST
                      </div>
                    )}
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center shadow-md ${
                        powerHoursStatus?.is_active 
                          ? 'bg-gradient-to-br from-yellow-500 to-orange-500'
                          : 'bg-gradient-to-br from-blue-500 to-indigo-500'
                      }`}>
                        <i className="fas fa-user-plus text-white"></i>
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-800 text-sm">New Lead Available</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] bg-gray-100 text-gray-700">
                            <i className="fas fa-globe mr-1"></i>
                            {lead.source || 'Website'}
                          </span>
                          {lead.meta_account_label && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] bg-indigo-100 text-indigo-700" title={`Source account: ${lead.meta_account_label}`} data-testid={`pool-meta-account-${lead.lead_id}`}>
                              <i className="fab fa-facebook-f mr-1"></i>
                              {lead.meta_account_label}
                            </span>
                          )}
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] bg-gray-100 text-gray-700">
                            <i className="fas fa-clock mr-1"></i>
                            {lead.created_at_display || new Date(lead.created_at).toLocaleTimeString('en-IN', { 
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                          {lead.interested_course && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] bg-blue-100 text-blue-700">
                              <i className="fas fa-graduation-cap mr-1"></i>
                              {lead.interested_course}
                            </span>
                          )}
                          {/* Live countdown for hybrid pool */}
                          {lead.pool_claim_expires_at && (() => {
                            const sec = getRemainingSec(lead);
                            if (sec === null) return null;
                            const expired = sec <= 0;
                            return (
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold ${expired ? 'bg-rose-100 text-rose-700 animate-pulse' : sec <= 60 ? 'bg-amber-100 text-amber-800 animate-pulse' : 'bg-emerald-100 text-emerald-700'}`}
                                data-testid={`pool-countdown-${lead.lead_id}`}
                                title={expired ? 'Auto-assigning soon' : 'Time left to claim before auto-assign'}
                              >
                                <i className="fas fa-stopwatch mr-1"></i>
                                {formatCountdown(sec)}
                              </span>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                    {user?.role === 'counselor' ? (
                    <button
                      onClick={() => handleClaimPowerHoursLead(lead.lead_id)}
                      disabled={claimingLead === lead.lead_id}
                      className="w-full py-2.5 px-4 rounded-lg font-bold text-white transition-all disabled:opacity-50"
                      style={{ 
                        background: claimingLead === lead.lead_id 
                          ? '#9CA3AF' 
                          : powerHoursStatus?.is_active
                            ? 'linear-gradient(135deg, #F59E0B 0%, #EA580C 100%)'
                            : 'linear-gradient(135deg, #3B82F6 0%, #6366F1 100%)',
                        boxShadow: powerHoursStatus?.is_active 
                          ? '0 4px 12px rgba(234, 88, 12, 0.3)'
                          : '0 4px 12px rgba(99, 102, 241, 0.3)'
                      }}
                      data-testid={`claim-lead-btn-${lead.lead_id}`}
                    >
                      {claimingLead === lead.lead_id ? (
                        <>
                          <i className="fas fa-spinner fa-spin mr-2"></i>
                          Claiming...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-hand-pointer mr-2"></i>
                          Claim This Lead
                        </>
                      )}
                    </button>
                    ) : (
                      <div className="w-full py-2 px-3 rounded-lg bg-gray-100 text-center text-xs text-gray-500">
                        <i className="fas fa-eye mr-1.5"></i>Awaiting counselor claim
                      </div>
                    )}
                  </div>
                ))}
              </div>
              
              <p className="text-xs text-center text-gray-500 mt-3">
                <i className="fas fa-info-circle mr-1"></i>
                {user?.role === 'counselor'
                  ? (powerHoursStatus?.is_active 
                    ? 'Leads are assigned first-come-first-serve. Connect the call during Power Hours to qualify for bonus.'
                    : 'Leads are assigned first-come-first-serve. Normal incentive applies.')
                  : 'These leads are visible to all counselors for claiming on a first-come-first-serve basis.'}
              </p>
            </div>
          )}

          {/* Empty State */}
          {powerHoursLeads.length === 0 && (
            <div className={`rounded-xl border p-4 mb-4 text-center ${
              powerHoursStatus?.is_active 
                ? 'bg-gradient-to-r from-yellow-50 to-orange-50 border-yellow-300'
                : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-300'
            }`}>
              <i className={`fas ${powerHoursStatus?.is_active ? 'fa-clock text-orange-400' : 'fa-inbox text-blue-400'} text-2xl mb-2`}></i>
              <p className="text-sm text-gray-700">
                {powerHoursStatus?.is_active 
                  ? 'No Power Hours leads available right now.'
                  : 'No pool leads available right now.'}
              </p>
              <p className="text-xs text-gray-500 mt-1">New leads will appear here {user?.role === 'counselor' ? 'for claiming' : 'when received'}.</p>
            </div>
          )}
        </div>
      )}

      {/* Overdue sub-filter chips — only on the Overdue tab.
          Counts come from the server (`tabCounts.overdue_types`) so the chip
          numbers reflect the FULL overdue corpus, not just the current page.
          Clicking a chip sets `overdueTypeFilter`, which triggers a server
          refetch with the narrower query. */}
      {activeTab === 'overdue' && (
        <div className="max-w-7xl mx-auto px-2 sm:px-4 pt-3" data-testid="overdue-type-filter">
          <div className="flex gap-1.5 items-center overflow-x-auto no-scrollbar pb-1">
            <span className="text-[10px] uppercase tracking-wide text-slate-500 font-medium mr-1 shrink-0">Filter:</span>
            {[
              { id: 'all', label: 'All' },
              { id: 'never_contacted', label: 'Never Connected' },
              { id: 'missed_followup', label: 'Missed Follow-up' },
              { id: 'missed_rnr', label: 'Missed RNR' },
              { id: 'missed_future', label: 'Missed Future' },
            ].map((f) => {
              const overdueTypes = tabCounts?.overdue_types || {};
              const count = f.id === 'all'
                ? (tabCounts?.overdue || 0)
                : (overdueTypes[f.id] || 0);
              const active = overdueTypeFilter === f.id;
              return (
                <button
                  key={f.id}
                  onClick={() => setOverdueTypeFilter(f.id)}
                  data-testid={`overdue-filter-${f.id}`}
                  className={`shrink-0 whitespace-nowrap px-2.5 py-1 rounded-full text-[11px] font-medium border transition-all ${
                    active
                      ? 'bg-slate-900 text-white border-slate-900'
                      : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  {f.label}
                  <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[9px] font-semibold ${
                    active ? 'bg-white/20' : 'bg-slate-100 text-slate-600'
                  }`}>{count}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Leads Grid */}
      <div className="max-w-7xl mx-auto px-2 sm:px-4 py-4">
        {loading && leads.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-2" style={{ borderColor: COLORS.accent, borderTopColor: 'transparent' }}></div>
          </div>
        ) : leads.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: COLORS.accentLight }}>
              <Users className="w-7 h-7" style={{ color: COLORS.accent }} />
            </div>
            <h3 className="text-base font-medium" style={{ color: COLORS.primary }}>No leads found</h3>
            <p className="text-sm mt-1" style={{ color: COLORS.textSecondary }}>
              {activeTab === 'new' ? 'No new leads to call' :
               activeTab === 'overdue' ? 'No overdue actions' :
               activeTab === 'followups' ? 'No scheduled follow-ups' :
               activeTab === 'rnr' ? 'No RNR leads to retry' :
               activeTab === 'future' ? 'No future intake leads' :
               'No bad leads'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {((activeTab === 'followups' || activeTab === 'future') ? sortedLeads : leads).map((lead) => {
              const styles = STATUS_STYLES[activeTab] || STATUS_STYLES['new'];
              const currentTab = TABS.find(t => t.id === activeTab);
              
              // NEW TAB ONLY: Get countdown for fresh leads (< 15 mins, no calls)
              const newLeadCountdown = activeTab === 'new' ? getNewLeadCountdown(lead) : null;
              const hasCountdown = newLeadCountdown !== null;
              
              // Get lead age info for display
              const leadAge = getLeadAge(lead);
              
              // FOLLOW-UP & FUTURE TAB: Get follow-up info (NO countdown)
              const followUpInfo = (activeTab === 'followups' || activeTab === 'future') ? getFollowUpInfo(lead) : null;
              
              // RNR TAB: Check if has today's follow-up (prefer rnr_follow_up_date, fall back to follow_up_date)
              const rnrIsToday = (() => {
                const rd = lead.rnr_follow_up_date || lead.follow_up_date;
                if (!rd) return false;
                const rnrDate = new Date(rd);
                if (isNaN(rnrDate.getTime())) return false;
                const now = new Date();
                const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
                const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
                return rnrDate >= todayStart && rnrDate <= todayEnd;
              })();
              
              return (
                <Card
                  key={lead.id}
                  className={`relative cursor-pointer transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 border overflow-hidden ${
                    // NEW TAB: Highlight based on countdown urgency (SLOW animations)
                    hasCountdown && newLeadCountdown.isCritical ? 'ring-2 ring-red-500 ring-offset-1 animate-pulse-medium' : 
                    hasCountdown && newLeadCountdown.isUrgent ? 'ring-2 ring-orange-500 ring-offset-1 animate-pulse-slow' :
                    hasCountdown ? 'ring-2 ring-yellow-400 ring-offset-1 animate-pulse-slow' :
                    // FOLLOW-UP & FUTURE TAB: Highlight today's follow-ups
                    followUpInfo?.isToday && !followUpInfo?.isPast ? 'ring-2 ring-green-400 ring-offset-1' :
                    followUpInfo?.isToday && followUpInfo?.isPast ? 'ring-2 ring-red-400 ring-offset-1' :
                    // RNR TAB: Highlight today's RNR retry follow-ups
                    activeTab === 'rnr' && rnrIsToday ? 'ring-2 ring-green-400 ring-offset-1' : ''
                  }`}
                  style={{ 
                    backgroundColor: 
                      // NEW TAB: Fresh leads (no calls) get green bg
                      activeTab === 'new' && (lead.total_calls || 0) === 0 ? '#F0FDF4' :
                      // FOLLOW-UP TAB: Today's follow-ups get colored bg
                      followUpInfo?.isToday && !followUpInfo?.isPast ? '#F0FDF4' :
                      followUpInfo?.isToday && followUpInfo?.isPast ? '#FEF2F2' :
                      // RNR TAB: Today's retry follow-ups get green bg
                      activeTab === 'rnr' && rnrIsToday ? '#F0FDF4' :
                      styles.bg, 
                    borderColor: 
                      activeTab === 'new' && (lead.total_calls || 0) === 0 ? '#86EFAC' :
                      followUpInfo?.isToday && !followUpInfo?.isPast ? '#86EFAC' :
                      followUpInfo?.isToday && followUpInfo?.isPast ? '#FCA5A5' :
                      // RNR TAB: Today's retry follow-ups get green border
                      activeTab === 'rnr' && rnrIsToday ? '#86EFAC' :
                      styles.border,
                  }}
                  onClick={() => openStudentProfile(lead)}
                  data-testid={`lead-tile-${lead.id}`}
                >
                  <CardContent className="p-2.5">
                    {/* NEW TAB ONLY: Countdown Badge for fresh leads within 15 minutes */}
                    {activeTab === 'new' && hasCountdown && (
                      <div className="absolute top-1 right-1 z-10">
                        <span 
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-bold ${
                            newLeadCountdown.isCritical ? 'animate-pulse-medium' : newLeadCountdown.isUrgent ? 'animate-pulse-slow' : ''
                          }`}
                          style={{ 
                            backgroundColor: newLeadCountdown.isCritical ? '#DC2626' : newLeadCountdown.isUrgent ? '#EA580C' : '#D97706',
                            color: '#FFFFFF', 
                            boxShadow: newLeadCountdown.isCritical ? '0 0 12px rgba(220, 38, 38, 0.6)' : '0 0 8px rgba(234, 88, 12, 0.4)'
                          }}
                          data-testid={`countdown-badge-${lead.id}`}
                        >
                          ⏱️ {newLeadCountdown.display}
                        </span>
                      </div>
                    )}
                    
                    {/* New Tab Badge - Differentiate Fresh Leads vs Callback Needed */}
                    {activeTab === 'new' && (
                      <div className="mb-1.5">
                        {(lead.total_calls || 0) === 0 ? (
                          <span 
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium"
                            style={{ backgroundColor: '#DCFCE7', color: '#16A34A' }}
                            data-testid={`new-badge-${lead.id}`}
                          >
                            <Bell className="w-2.5 h-2.5" />
                            Fresh Lead
                          </span>
                        ) : (
                          <span 
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium"
                            style={{ backgroundColor: '#FEF3C7', color: '#D97706' }}
                            data-testid={`new-badge-${lead.id}`}
                          >
                            <PhoneOff className="w-2.5 h-2.5" />
                            Callback Needed ({lead.total_calls} calls)
                          </span>
                        )}
                      </div>
                    )}
                    
                    {/* Reassignment Badge — takes precedence over Claim/Auto-assign badges
                        because once a lead is reassigned by Admin/OH, the claim history
                        is stale (the new counsellor didn't claim it themselves). */}
                    {lead.reassigned_at && lead.reassigned_by_name ? (
                      <div className="mb-1.5">
                        <span
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium bg-amber-50 text-amber-800 border border-amber-200"
                          data-testid={`reassigned-badge-${lead.id}`}
                          title={`Reassigned${lead.reassigned_from_counselor_name ? ` from ${lead.reassigned_from_counselor_name}` : ''} to ${lead.assigned_counselor_name || ''} by ${lead.reassigned_by_name}${lead.reassigned_by_role ? ` (${String(lead.reassigned_by_role).replace(/_/g, ' ')})` : ''}`}
                        >
                          <i className="fas fa-exchange-alt text-[8px]"></i>
                          Reassigned by {lead.reassigned_by_name}
                          {lead.reassigned_by_role ? (
                            <span className="ml-0.5 opacity-75">({String(lead.reassigned_by_role).replace(/_/g, ' ')})</span>
                          ) : null}
                          {' at '}
                          {(() => {
                            try {
                              const raw = String(lead.reassigned_at).replace('Z','').replace('+00:00','').replace('+05:30','');
                              return new Date(raw).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
                            } catch { return ''; }
                          })()}
                        </span>
                      </div>
                    ) : (lead.pool_claimed || lead.power_hours_claimed) && lead.power_hours_claimed_at && (
                      <div className="mb-1.5">
                        {lead.auto_assigned_after_pool ? (
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium bg-slate-100 text-slate-600"
                            data-testid={`auto-assigned-badge-${lead.id}`}
                            title="This lead was auto-assigned by the CRM via round-robin (not manually claimed)"
                          >
                            <i className="fas fa-robot text-[8px]"></i>
                            Auto-assigned by CRM
                          </span>
                        ) : (
                          <span 
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium bg-indigo-50 text-indigo-700"
                            data-testid={`claimed-badge-${lead.id}`}
                          >
                            <i className="fas fa-hand-pointer text-[8px]"></i>
                            Claimed {lead.assigned_counselor_name ? `by ${lead.assigned_counselor_name}` : ''} at {(() => {
                              try {
                                const raw = String(lead.power_hours_claimed_at).replace('Z','').replace('+00:00','').replace('+05:30','');
                                return new Date(raw).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
                              } catch { return ''; }
                            })()}
                          </span>
                        )}
                      </div>
                    )}
                    
                    {/* Overdue Type Badge - Only in Overdue tab */}
                    {activeTab === 'overdue' && (
                      <div className="mb-1.5">
                        {getOverdueType(lead) === 'missed_future' ? (
                          <span 
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium"
                            style={{ backgroundColor: '#FEF3C7', color: '#B45309' }}
                            data-testid={`overdue-badge-${lead.id}`}
                          >
                            <Clock className="w-2.5 h-2.5" />
                            Missed Future Follow-up
                            {lead.follow_up_date && (
                              <span className="ml-1">• {formatToIST(lead.follow_up_date)}</span>
                            )}
                          </span>
                        ) : getOverdueType(lead) === 'never_contacted' ? (
                          <span 
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium"
                            style={{ backgroundColor: '#FEF3C7', color: '#D97706' }}
                            data-testid={`overdue-badge-${lead.id}`}
                          >
                            <AlertCircle className="w-2.5 h-2.5" />
                            Never Connected
                          </span>
                        ) : getOverdueType(lead) === 'missed_rnr' ? (
                          <span 
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium"
                            style={{ backgroundColor: '#FFF7ED', color: '#EA580C' }}
                            data-testid={`overdue-badge-${lead.id}`}
                          >
                            <PhoneOff className="w-2.5 h-2.5" />
                            Missed RNR Follow-up • {lead.rnr_consecutive || 0}/{rnrThreshold}
                            {lead.rnr_follow_up_date && (
                              <span className="ml-1">• {formatToIST(lead.rnr_follow_up_date)}</span>
                            )}
                          </span>
                        ) : (
                          <span 
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium"
                            style={{ backgroundColor: '#FEE2E2', color: '#DC2626' }}
                            data-testid={`overdue-badge-${lead.id}`}
                          >
                            <Calendar className="w-2.5 h-2.5" />
                            Missed Follow-up
                            {lead.follow_up_date && (
                              <span className="ml-1">• {formatToIST(lead.follow_up_date)}</span>
                            )}
                          </span>
                        )}
                      </div>
                    )}
                    
                    {/* Follow-ups Tab Badge - Show Today/Tomorrow/etc with time info */}
                    {/* FOLLOW-UP TAB: Badge showing Today/Tomorrow/etc - NO countdown */}
                    {activeTab === 'followups' && followUpInfo && (
                      <div className="mb-1.5">
                        <span 
                          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium ${
                            followUpInfo.isToday && followUpInfo.isPast ? 'animate-pulse-slow' : ''
                          }`}
                          style={{ backgroundColor: followUpInfo.badgeBg, color: followUpInfo.badgeColor }}
                          data-testid={`followup-badge-${lead.id}`}
                        >
                          {followUpInfo.badge}
                          {followUpInfo.isToday && !followUpInfo.isPast && (
                            <span className="font-bold ml-1">
                              {lead.follow_up_time_specified ? `⏰ ${followUpInfo.timeStr}` : 'Anytime today'}
                            </span>
                          )}
                        </span>
                      </div>
                    )}
                    
                    {/* FUTURE TAB: Badge showing follow-up date/time */}
                    {activeTab === 'future' && followUpInfo && (
                      <div className="mb-1.5">
                        <span 
                          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium ${
                            followUpInfo.isToday && followUpInfo.isPast ? 'animate-pulse-slow' : ''
                          }`}
                          style={{ backgroundColor: followUpInfo.isToday ? followUpInfo.badgeBg : '#FEF3C7', color: followUpInfo.isToday ? followUpInfo.badgeColor : '#B45309' }}
                          data-testid={`future-badge-${lead.id}`}
                        >
                          <Clock className="w-2.5 h-2.5" />
                          {followUpInfo.isToday ? `Future Follow-up Today` : followUpInfo.badge.replace('📅 ', '').replace('📆 ', '')}
                          {followUpInfo.isToday && !followUpInfo.isPast && (
                            <span className="font-bold ml-1">@ {followUpInfo.timeStr}</span>
                          )}
                          {!followUpInfo.isToday && (
                            <span className="ml-1">• {formatToIST(lead.follow_up_date)}</span>
                          )}
                          {lead.eligible_intake && (
                            <span className="ml-1">• {lead.eligible_intake.replace(/_/g, ' ')}</span>
                          )}
                        </span>
                      </div>
                    )}
                    {activeTab === 'future' && !followUpInfo && (
                      <div className="mb-1.5">
                        <span 
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium"
                          style={{ backgroundColor: '#FEF3C7', color: '#B45309' }}
                          data-testid={`future-badge-${lead.id}`}
                        >
                          <Clock className="w-2.5 h-2.5" />
                          Future Prospective
                          {lead.eligible_intake && (
                            <span className="ml-1">• {lead.eligible_intake.replace(/_/g, ' ')}</span>
                          )}
                        </span>
                      </div>
                    )}
                    
                    {/* RNR Tab Badge - Show scheduled retry date */}
                    {activeTab === 'rnr' && (() => {
                      // Prefer rnr_follow_up_date, fall back to follow_up_date (some flows only set the latter)
                      const rnrDate = lead.rnr_follow_up_date || lead.follow_up_date;
                      if (!rnrDate) return (
                        <div className="mb-1.5">
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium"
                            style={{ backgroundColor: '#FFF7ED', color: '#EA580C' }}
                            data-testid={`rnr-badge-${lead.id}`}>
                            <PhoneOff className="w-2.5 h-2.5" />
                            RNR {lead.rnr_consecutive || 0}/{rnrThreshold} • No retry scheduled
                          </span>
                        </div>
                      );
                      
                      // Parse the date - stored as naive IST, parse as local time
                      const rnrDateParsed = new Date(rnrDate);
                      if (isNaN(rnrDateParsed.getTime())) return null;
                      
                      const now = new Date();
                      
                      // Check if today in local time (IST)
                      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
                      const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
                      const isToday = rnrDateParsed >= todayStart && rnrDateParsed <= todayEnd;
                      const isPast = rnrDateParsed < now;
                      
                      // Format time in local time (IST)
                      let hours = rnrDateParsed.getHours();
                      const minutes = rnrDateParsed.getMinutes().toString().padStart(2, '0');
                      const ampm = hours >= 12 ? 'PM' : 'AM';
                      hours = hours % 12 || 12;
                      const timeStr = `${hours}:${minutes} ${ampm}`;
                      
                      // Format date for non-today
                      const dateDisplay = `${rnrDateParsed.getDate()} ${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][rnrDateParsed.getMonth()]}`;
                      
                      return (
                        <div className="mb-1.5">
                          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium ${isToday && !isPast ? 'animate-pulse-slow' : ''}`}
                            style={{ 
                              backgroundColor: isToday ? '#DCFCE7' : '#FFF7ED',
                              color: isToday ? '#16A34A' : '#EA580C'
                            }}
                            data-testid={`rnr-badge-${lead.id}`}>
                            <Phone className="w-2.5 h-2.5" />
                            RNR {lead.rnr_consecutive || 0}/{rnrThreshold}
                            {isToday ? ` • Retry TODAY @ ${timeStr}` : ` • Retry: ${dateDisplay} @ ${timeStr}`}
                          </span>
                        </div>
                      );
                    })()}
                    
                    {/* Row 1: Name + Star + Reassign (Admin) */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-sm truncate" style={{ color: COLORS.primary }}>
                          {lead.full_name}
                        </h3>
                        <p className="text-[10px] font-mono" style={{ color: COLORS.accent }}>
                          {lead.student_id || 'N/A'}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          className="p-2 sm:p-1 rounded-full transition-all hover:bg-gray-100"
                          onClick={(e) => handleToggleProspective(e, lead)}
                          title={lead.is_prospective ? 'Remove Prospective' : 'Mark Prospective'}
                          data-testid={`toggle-prospective-${lead.id}`}
                        >
                          <Star className={`w-3.5 h-3.5 transition-colors ${
                            lead.is_prospective ? 'text-yellow-500 fill-yellow-500' : 'text-gray-400 hover:text-gray-500'
                          }`} />
                        </button>
                        {/* Reassign Button - Admin only (all lead tabs) */}
                        {isAdmin && (activeTab === 'new' || activeTab === 'overdue' || activeTab === 'bad' || activeTab === 'followups' || activeTab === 'rnr' || activeTab === 'future') && (
                          <button
                            className="p-2 sm:p-1 rounded-full transition-all hover:bg-blue-50"
                            onClick={(e) => openReassignDialog(e, lead)}
                            title="Reassign Lead"
                            data-testid={`reassign-btn-${lead.id}`}
                          >
                            <UserPlus className="w-3.5 h-3.5 text-blue-500 hover:text-blue-600" />
                          </button>
                        )}
                      </div>
                    </div>
                    
                    {/* Row 2: Program, Country, Phone - condensed */}
                    <div className="mt-1.5 space-y-0.5">
                      <p className="text-xs truncate" style={{ color: COLORS.textSecondary }}>
                        {/* Show initial_message preview if country/program not set, otherwise show country + program */}
                        {(!lead.desired_country && !lead.desired_course && lead.initial_message) ? (
                          <span className="italic text-gray-500">"{lead.initial_message.slice(0, 60)}{lead.initial_message.length > 60 ? '...' : ''}"</span>
                        ) : (
                          <>
                            {lead.desired_course || 'Not specified'}
                            {lead.desired_country && <span> • {lead.desired_country}</span>}
                          </>
                        )}
                      </p>
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-medium" style={{ color: COLORS.primary }}>
                          {lead.phone}
                        </p>
                        <div className="flex items-center gap-1.5 text-[10px]" style={{ color: COLORS.textSecondary }}>
                          {(lead.total_calls || 0) > 0 && (
                            <span className="flex items-center gap-0.5">
                              <Phone className="w-2.5 h-2.5" /> {lead.total_calls}
                            </span>
                          )}
                          {isAdmin && lead.assigned_counselor_name && (
                            <span className="truncate max-w-[80px]">{lead.assigned_counselor_name}</span>
                          )}
                        </div>
                      </div>
                      
                      {/* Lead Created Date/Time - ALWAYS SHOWN */}
                      <div 
                        className="flex items-center justify-between mt-1.5 px-2 py-1 rounded-md"
                        style={{ backgroundColor: leadAge.bgColor }}
                      >
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3" style={{ color: leadAge.color }} />
                          <span 
                            className="text-[11px] font-semibold"
                            style={{ color: leadAge.color }}
                          >
                            {leadAge.text}
                          </span>
                        </div>
                        <span 
                          className="text-[9px]"
                          style={{ color: leadAge.color }}
                        >
                          {formatToIST(lead.created_at)}
                        </span>
                      </div>
                      
                      {/* Follow-up Date - only shown on tabs where there's no top badge */}
                      {lead.follow_up_date && activeTab !== 'followups' && activeTab !== 'rnr' && activeTab !== 'overdue' && activeTab !== 'future' && (
                        <div 
                          className="flex items-center justify-between mt-1 px-2 py-1 rounded-md"
                          style={{ backgroundColor: followUpInfo ? followUpInfo.badgeBg : '#FEF3C7' }}
                        >
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" style={{ color: followUpInfo ? followUpInfo.badgeColor : '#D97706' }} />
                            <span 
                              className="text-[11px] font-semibold"
                              style={{ color: followUpInfo ? followUpInfo.badgeColor : '#D97706' }}
                            >
                              Follow-up{followUpInfo ? `: ${followUpInfo.timeStr}` : ''}
                            </span>
                          </div>
                          <span 
                            className="text-[9px]"
                            style={{ color: followUpInfo ? followUpInfo.badgeColor : '#D97706' }}
                          >
                            {formatToIST(lead.follow_up_date)}
                          </span>
                        </div>
                      )}
                      
                      {/* Lead Source & Ad Campaign Info */}
                      {lead.lead_source && (
                        <div className="flex items-center justify-end gap-1 mt-1 flex-wrap">
                          <span className="bg-gray-100 px-1.5 py-0.5 rounded text-[9px] text-gray-600">
                            {lead.lead_source}
                          </span>
                          {/* Meta account source (Patrick's Meta / Gradbud Meta) */}
                          {lead.meta_account_label && (
                            <span className="bg-indigo-100 px-1.5 py-0.5 rounded text-[9px] text-indigo-700 flex items-center gap-0.5" title={`Source account: ${lead.meta_account_label}`} data-testid={`lead-meta-account-${lead.id}`}>
                              <i className="fab fa-facebook-f text-[8px]"></i>
                              {lead.meta_account_label}
                            </span>
                          )}
                          {/* Show ad campaign badge if from CTWA */}
                          {lead.ctwa_ad_campaign_name && (
                            <span className="bg-blue-100 px-1.5 py-0.5 rounded text-[9px] text-blue-700 flex items-center gap-0.5" title={`Campaign: ${lead.ctwa_ad_campaign_name}${lead.ctwa_ad_name ? ` | Ad: ${lead.ctwa_ad_name}` : ''}`}>
                              <i className="fab fa-facebook-f text-[8px]"></i>
                              Ad
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* Action Buttons - Mobile Optimized (larger tap targets on phones) */}
                    <div className="flex gap-1.5 mt-2 pt-1.5" style={{ borderTop: `1px solid ${COLORS.border}` }}>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 h-9 sm:h-7 text-xs sm:text-[10px] px-2"
                        style={{ backgroundColor: '#ECFDF5', borderColor: '#A7F3D0', color: '#059669' }}
                        onClick={(e) => handleCall(e, lead)}
                        data-testid={`call-btn-${lead.id}`}
                      >
                        <Phone className="w-3.5 h-3.5 sm:w-3 sm:h-3 mr-1" />
                        Call
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 h-9 sm:h-7 text-xs sm:text-[10px] px-2"
                        style={{ backgroundColor: '#ECFDF5', borderColor: '#6EE7B7', color: '#047857' }}
                        onClick={(e) => handleWhatsApp(e, lead)}
                        data-testid={`whatsapp-btn-${lead.id}`}
                      >
                        <MessageCircle className="w-3.5 h-3.5 sm:w-3 sm:h-3 mr-1" />
                        WA
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 h-9 sm:h-7 text-xs sm:text-[10px] px-2"
                        style={{ backgroundColor: COLORS.accentLight, borderColor: COLORS.accent, color: COLORS.accent }}
                        onClick={(e) => openCallResponseDialog(e, lead)}
                        data-testid={`update-call-btn-${lead.id}`}
                      >
                        <ClipboardEdit className="w-3.5 h-3.5 sm:w-3 sm:h-3 mr-1" />
                        Update
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
        
        {/* Pagination UI */}
        {!loading && pagination.total_count > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4 pt-4 border-t" style={{ borderColor: COLORS.border }}>
            {/* Info Text */}
            <p className="text-xs sm:text-sm" style={{ color: COLORS.textSecondary }}>
              Showing {((pagination.page - 1) * pagination.page_size) + 1}–{Math.min(pagination.page * pagination.page_size, pagination.total_count)} of <strong style={{ color: COLORS.primary }}>{pagination.total_count}</strong> leads
            </p>
            
            {/* Pagination Controls */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={goToPrevPage}
                disabled={!pagination.has_prev}
                className="h-8 px-2 sm:px-3 text-xs"
                style={{ borderColor: COLORS.border }}
                data-testid="pagination-prev"
              >
                <ChevronLeft className="w-4 h-4 sm:mr-1" />
                <span className="hidden sm:inline">Prev</span>
              </Button>
              
              {/* Page Numbers (desktop only) */}
              <div className="hidden sm:flex items-center gap-1">
                {pagination.total_pages <= 5 ? (
                  // Show all pages if 5 or fewer
                  [...Array(pagination.total_pages)].map((_, i) => (
                    <Button
                      key={i + 1}
                      variant={pagination.page === i + 1 ? "default" : "outline"}
                      size="sm"
                      onClick={() => goToPage(i + 1)}
                      className="h-8 w-8 p-0 text-xs"
                      style={pagination.page === i + 1 ? { backgroundColor: COLORS.accent } : { borderColor: COLORS.border }}
                      data-testid={`pagination-page-${i + 1}`}
                    >
                      {i + 1}
                    </Button>
                  ))
                ) : (
                  // Show limited pages with ellipsis
                  <>
                    <Button
                      variant={pagination.page === 1 ? "default" : "outline"}
                      size="sm"
                      onClick={() => goToPage(1)}
                      className="h-8 w-8 p-0 text-xs"
                      style={pagination.page === 1 ? { backgroundColor: COLORS.accent } : { borderColor: COLORS.border }}
                    >
                      1
                    </Button>
                    {pagination.page > 3 && <span className="text-xs px-1" style={{ color: COLORS.textSecondary }}>...</span>}
                    {pagination.page > 2 && pagination.page < pagination.total_pages - 1 && (
                      <>
                        {pagination.page > 3 && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => goToPage(pagination.page - 1)}
                            className="h-8 w-8 p-0 text-xs"
                            style={{ borderColor: COLORS.border }}
                          >
                            {pagination.page - 1}
                          </Button>
                        )}
                        <Button
                          variant="default"
                          size="sm"
                          className="h-8 w-8 p-0 text-xs"
                          style={{ backgroundColor: COLORS.accent }}
                        >
                          {pagination.page}
                        </Button>
                        {pagination.page < pagination.total_pages - 2 && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => goToPage(pagination.page + 1)}
                            className="h-8 w-8 p-0 text-xs"
                            style={{ borderColor: COLORS.border }}
                          >
                            {pagination.page + 1}
                          </Button>
                        )}
                      </>
                    )}
                    {pagination.page < pagination.total_pages - 2 && <span className="text-xs px-1" style={{ color: COLORS.textSecondary }}>...</span>}
                    <Button
                      variant={pagination.page === pagination.total_pages ? "default" : "outline"}
                      size="sm"
                      onClick={() => goToPage(pagination.total_pages)}
                      className="h-8 w-8 p-0 text-xs"
                      style={pagination.page === pagination.total_pages ? { backgroundColor: COLORS.accent } : { borderColor: COLORS.border }}
                    >
                      {pagination.total_pages}
                    </Button>
                  </>
                )}
              </div>
              
              {/* Mobile: Show page X of Y */}
              <span className="sm:hidden text-xs px-2" style={{ color: COLORS.textSecondary }}>
                {pagination.page} / {pagination.total_pages}
              </span>
              
              <Button
                variant="outline"
                size="sm"
                onClick={goToNextPage}
                disabled={!pagination.has_next}
                className="h-8 px-2 sm:px-3 text-xs"
                style={{ borderColor: COLORS.border }}
                data-testid="pagination-next"
              >
                <span className="hidden sm:inline">Next</span>
                <ChevronRight className="w-4 h-4 sm:ml-1" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Add Lead Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={(open) => { setAddDialogOpen(open); if (open) fetchPrograms(); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <i className="fas fa-user-plus text-indigo-600"></i>
              Add New Lead
            </DialogTitle>
            <DialogDescription>
              A unique Student ID will be automatically generated.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Full Name *</Label>
                <Input
                  placeholder="Enter full name"
                  value={newLead.full_name}
                  onChange={(e) => setNewLead({...newLead, full_name: e.target.value})}
                />
              </div>
              <div>
                <Label>Phone *</Label>
                <Input
                  placeholder="+91 XXXXX XXXXX"
                  value={newLead.phone}
                  onChange={(e) => setNewLead({...newLead, phone: e.target.value})}
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  placeholder="email@example.com"
                  value={newLead.email}
                  onChange={(e) => setNewLead({...newLead, email: e.target.value})}
                />
              </div>
              <div>
                <Label>Lead Source</Label>
                <Select value={newLead.lead_source} onValueChange={(v) => setNewLead({...newLead, lead_source: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Manual</SelectItem>
                    <SelectItem value="instagram">Instagram</SelectItem>
                    <SelectItem value="facebook">Facebook</SelectItem>
                    <SelectItem value="website">Website</SelectItem>
                    <SelectItem value="referral">Referral</SelectItem>
                    <SelectItem value="walk_in">Walk-in</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Program</Label>
                <CreatableSelect
                  options={programs}
                  value={newLead.desired_course}
                  onValueChange={(v) => setNewLead({...newLead, desired_course: v})}
                  onCreateOption={handleAddProgram}
                  placeholder="Search or add program..."
                  searchPlaceholder="Type to search or add..."
                  createLabel="Add new program"
                  canCreate={canAddProgram}
                />
              </div>
              <div>
                <Label>Country</Label>
                <SearchableSelect
                  options={COUNTRIES}
                  value={newLead.desired_country}
                  onValueChange={(v) => setNewLead({...newLead, desired_country: v})}
                  placeholder="Search country..."
                  searchPlaceholder="Type to search countries..."
                  emptyMessage="No country found."
                />
              </div>
              <div>
                <Label>Eligible Intake</Label>
                <Select value={newLead.eligible_intake} onValueChange={(v) => setNewLead({...newLead, eligible_intake: v})}>
                  <SelectTrigger><SelectValue placeholder="Select intake" /></SelectTrigger>
                  <SelectContent>
                    {intakes.map((intake) => (
                      <SelectItem key={intake} value={intake}>{intake}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Admin: Manual Counselor Assignment */}
              {isAdmin && counsellors.length > 0 && (
                <div className="col-span-2">
                  <Label>Assign to Counselor (Optional)</Label>
                  <select
                    value={newLead.assign_to_counselor_id || ''}
                    onChange={(e) => setNewLead({...newLead, assign_to_counselor_id: e.target.value || null})}
                    className="w-full h-9 text-sm bg-white border border-gray-200 rounded-md px-3 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="">Auto-assign (Round Robin)</option>
                    {counsellors.map(c => (
                      <option key={c.id} value={c.id}>{c.full_name}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Leave empty for automatic assignment</p>
                </div>
              )}
              
              <div className="col-span-2">
                <Label>Notes</Label>
                <Textarea
                  placeholder="Add any notes..."
                  value={newLead.notes}
                  onChange={(e) => setNewLead({...newLead, notes: e.target.value})}
                  rows={2}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleAddLead}
              disabled={addingLead || !newLead.full_name || !newLead.phone}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {addingLead ? (
                <><i className="fas fa-spinner fa-spin mr-2"></i> Creating...</>
              ) : (
                <><i className="fas fa-plus mr-2"></i> Create Lead</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update Call Response Dialog (shared guided flow) */}
      <CallResponseDialog
        open={callDialogOpen}
        onOpenChange={setCallDialogOpen}
        lead={selectedLead}
        onSaved={() => { setSelectedLead(null); fetchLeads(); }}
      />


      {/* Reassign Lead Dialog - Admin Only */}
      <Dialog open={reassignDialogOpen} onOpenChange={setReassignDialogOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ color: COLORS.primary }}>
              <UserPlus className="w-5 h-5 text-blue-500" />
              Reassign Lead
            </DialogTitle>
            <DialogDescription>
              Reassign "{reassignLead?.full_name}" to another counselor. The new counselor will be notified.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-3">
            {/* Current Counselor Info */}
            {reassignLead?.assigned_counselor_name && (
              <div className="p-3 rounded-lg" style={{ backgroundColor: COLORS.background }}>
                <p className="text-xs" style={{ color: COLORS.textSecondary }}>Currently assigned to:</p>
                <p className="font-medium text-sm" style={{ color: COLORS.primary }}>
                  {reassignLead.assigned_counselor_name}
                </p>
              </div>
            )}
            
            {/* Select New Counselor */}
            <div className="space-y-2">
              <Label className="text-sm font-medium" style={{ color: COLORS.primary }}>
                Select New Counselor <span className="text-red-500">*</span>
              </Label>
              <Select value={reassignCounselorId} onValueChange={setReassignCounselorId}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Choose a counselor..." />
                </SelectTrigger>
                <SelectContent>
                  {counsellors
                    .filter(c => c.id !== reassignLead?.assigned_counselor_id)
                    .map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.full_name}
                      </SelectItem>
                    ))
                  }
                </SelectContent>
              </Select>
            </div>
            
            {/* Reason (Optional) */}
            <div className="space-y-2">
              <Label className="text-sm font-medium" style={{ color: COLORS.primary }}>
                Reason for Reassignment <span className="text-xs" style={{ color: COLORS.textSecondary }}>(Optional)</span>
              </Label>
              <Textarea
                value={reassignReason}
                onChange={(e) => setReassignReason(e.target.value)}
                placeholder="E.g., Workload balancing, counselor specialty match..."
                rows={2}
                className="text-sm resize-none"
              />
            </div>
            
            {/* Info Note */}
            <div className="p-3 rounded-lg flex items-start gap-2" style={{ backgroundColor: '#EFF6FF' }}>
              <Bell className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs" style={{ color: '#1D4ED8' }}>
                The new counselor will receive both an in-app notification and a push notification about this assignment.
              </p>
            </div>
          </div>
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setReassignDialogOpen(false)}
              disabled={reassigning}
              className="flex-1 sm:flex-none"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmitReassign}
              disabled={reassigning || !reassignCounselorId}
              className="flex-1 sm:flex-none"
              style={{ backgroundColor: COLORS.accent }}
            >
              {reassigning ? 'Reassigning...' : 'Reassign Lead'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
